'use client';

import React, { memo, useCallback, useMemo, useState } from 'react';
import { isScored } from '#lib/leasing/leadStatus';
import { isHumanRemoved } from '#lib/leasing/leadStatus';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { EntityView } from '../EntityView';
import { useLeadDependencyHydration } from '../useLeadDependencyHydration';
import { Grid } from '@/components/portal/Grid';
import { ViewModeGraphicToggle, type InventoryVisualMode } from '@/components/portal/ViewModeGraphicToggle';
import { supportsInventoryViewModeToggle } from '@/components/portal/inventoryViewModeCapabilities';
import { Building2, CheckCircle2, Gauge, Target } from '@/components/ui/lucide-memo';
import type { LeasingLeadFilters } from '#types/types';

const translations = {
  en: {
    title: 'All leads',
    breadcrumb: 'Inventory / Leads',
    searchPlaceholder: 'Search leads...',
    sortOptions: {
      fitScore: 'Fit Score',
      updatedAt: 'Last Updated',
      createdAt: 'Created Date',
    },
    filterOptions: {
      brand: 'Brand',
      status: 'Status',
      fit: 'Lead fit',
      campaign: 'Campaign',
    },
  },
  fr: {
    title: 'Tous les leads',
    breadcrumb: 'Inventaire / Leads',
    searchPlaceholder: 'Rechercher des leads...',
    sortOptions: {
      fitScore: 'Score de fit',
      updatedAt: 'Derniere maj',
      createdAt: 'Date creation',
    },
    filterOptions: {
      brand: 'Marque',
      status: 'Statut',
      fit: 'Fit lead',
      campaign: 'Campagne',
    },
  },
} as const;

// The card owns the click handler so the memoized LeadRenderer under EntityView keeps a stable
// onClick reference across grid re-renders, instead of one new closure per card per render.
const LeadGridCard = memo(({ leadId, onOpen }: { leadId: string; onOpen: (leadId: string) => void }) => {
  const handleClick = useCallback(() => onOpen(leadId), [leadId, onOpen]);
  return <EntityView type="Lead" entityId={leadId} variant="card" onClick={handleClick} />;
});
LeadGridCard.displayName = 'LeadGridCard';

interface LeadsGridProps {
  initialFilters?: Partial<LeasingLeadFilters>;
  lockedFilters?: Partial<LeasingLeadFilters>;
  scopedIds?: string[] | null;
  assetLedgerTitle?: string;
  assetLedgerBreadcrumb?: string;
  renderCustomToolbarHeader?: () => React.ReactNode;
  disableManualRefresh?: boolean;
  onManualRefresh?: () => void | Promise<void>;
  viewModeVisual?: InventoryVisualMode;
  onViewModeVisualChange?: (mode: InventoryVisualMode) => void;
}

const LeadsGrid = memo(({
  initialFilters,
  lockedFilters,
  scopedIds,
  assetLedgerTitle,
  assetLedgerBreadcrumb,
  renderCustomToolbarHeader,
  disableManualRefresh,
  onManualRefresh,
  viewModeVisual,
  onViewModeVisualChange,
}: LeadsGridProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const [localViewModeVisual, setLocalViewModeVisual] = useState<InventoryVisualMode>('grid');
  const handleVisibleIdsChange = useLeadDependencyHydration();
  const resolvedViewModeVisual = viewModeVisual ?? localViewModeVisual;

  const handleViewModeVisualChange = useCallback((mode: InventoryVisualMode) => {
    onViewModeVisualChange?.(mode);
    if (!onViewModeVisualChange) {
      setLocalViewModeVisual(mode);
    }
  }, [onViewModeVisualChange]);

  const effectiveInitialFilters = useMemo<Partial<LeasingLeadFilters>>(
    () =>
      initialFilters === undefined
        ? {
            sortBy: 'fitScore',
            sortDir: 'desc',
          }
        : {
            ...initialFilters,
          },
    [initialFilters]
  );

  const sortOptions = useMemo(
    () => [
      { label: t.sortOptions.fitScore, field: 'fitScore', localAccessor: (lead: any) => (isScored(lead?.status) ? lead?.fitScore ?? null : null) },
      { label: t.sortOptions.updatedAt, field: 'updatedAt' },
      { label: t.sortOptions.createdAt, field: 'createdAt' },
    ],
    [t.sortOptions.createdAt, t.sortOptions.fitScore, t.sortOptions.updatedAt]
  );

  const filterOptions = useMemo(
    () => [
      { key: 'brandName', label: t.filterOptions.brand, icon: <Building2 size={16} strokeWidth={1.9} /> },
      { key: 'status', label: t.filterOptions.status, icon: <CheckCircle2 size={16} strokeWidth={1.9} /> },
      { key: 'fitScore', label: t.filterOptions.fit, icon: <Gauge size={16} strokeWidth={1.9} /> },
      { key: 'lastCampaignId', label: t.filterOptions.campaign, icon: <Target size={16} strokeWidth={1.9} /> },
    ],
    [t.filterOptions.brand, t.filterOptions.campaign, t.filterOptions.fit, t.filterOptions.status]
  );

  const handleOpenLead = useCallback((leadId: string) => {
    if (!leadId) return;
    void kernel.current?.focusEntityUI('Lead', leadId);
  }, [kernel]);
  // DISMISSED stays VISIBLE, it just sinks to the bottom (see the set-aside rank in Table.tsx).
  // It is now also the status the judge gives a brand that does not fit the asset, so hiding it
  // would silently drop scored, well-reasoned candidates out of the shortlist the manager is
  // meant to review. Only DISCARDED, the legacy status nothing writes any more, is hidden.
  const hideDiscardedLeads = useCallback(
    (lead: { status?: string | null }) => !isHumanRemoved(lead?.status),
    [],
  );
  const canShowViewModeToggle = supportsInventoryViewModeToggle('Lead') && typeof onViewModeVisualChange === 'function';
  const composedToolbarHeader = useCallback(() => {
    if (!canShowViewModeToggle && !renderCustomToolbarHeader) return null;
    return (
      <>
        {canShowViewModeToggle && (
          <ViewModeGraphicToggle
            mode={resolvedViewModeVisual}
            onModeChange={handleViewModeVisualChange}
            className={renderCustomToolbarHeader ? 'mr-1' : undefined}
          />
        )}
        {renderCustomToolbarHeader?.()}
      </>
    );
  }, [
    canShowViewModeToggle,
    handleViewModeVisualChange,
    renderCustomToolbarHeader,
    resolvedViewModeVisual,
  ]);
  const effectiveToolbarHeader =
    canShowViewModeToggle || renderCustomToolbarHeader ? composedToolbarHeader : undefined;

  const renderLeadCard = useCallback(
    (lead: { id: string }) => <LeadGridCard key={lead.id} leadId={lead.id} onOpen={handleOpenLead} />,
    [handleOpenLead]
  );

  return (
    <Grid
      entityType="Lead"
      pageSize={24}
      initialFilters={effectiveInitialFilters}
      lockedFilters={lockedFilters}
      scopedIds={scopedIds}
      disableManualRefresh={disableManualRefresh}
      onManualRefresh={onManualRefresh}
      sortOptions={sortOptions}
      filterOptions={filterOptions}
      assetLedgerTitle={assetLedgerTitle || t.title}
      assetLedgerBreadcrumb={assetLedgerBreadcrumb || t.breadcrumb}
      searchPlaceholder={t.searchPlaceholder}
      renderToolbarExtras={effectiveToolbarHeader}
      gridClass="grid-cols-[repeat(auto-fit,minmax(150px,1fr))]"
      itemPredicate={hideDiscardedLeads}
      onVisibleItemIdsChange={handleVisibleIdsChange}
      renderCard={renderLeadCard}
    />
  );
});

LeadsGrid.displayName = 'LeadsGrid';
export { LeadsGrid };
